import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search,
  X,
  MessageSquare,
  Send,
  CheckSquare,
  Square,
  Clock,
  RotateCcw,
  Save,
  Users,
  Smartphone,
  UserCheck,
  UserPlus,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Plus,
  Upload,
  Eye,
  EyeOff,
  Sparkles,
  Library,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { playerProfileService } from '../../apis/playerProfileService';
import { getBulkSmsPayload, toastBulkSmsResult } from '../../utils/smsSendResult';
import SmsMessageOptimizerModal from './SmsMessageOptimizerModal';
import SmsPresetsLibraryModal from './SmsPresetsLibraryModal';

const CARRIER_FLAGGED_PATTERNS = [
  { regex: /\b\$\d+k?\s*gtd\b/i, label: 'Prize Guarantee ($... GTD)' },
  { regex: /\b\$\d+[,.]?\d*\s*(jackpot|bounty|prize|bonus|freeroll)\b/i, label: 'Monetary Prize Amount' },
  { regex: /\bjackpot\b/i, label: 'Jackpot' },
  { regex: /\bcash\s+games?\b/i, label: 'Cash Game' },
  { regex: /\b\$1\/\$2\b/i, label: 'Betting Stakes' },
  { regex: /\b\$1\/\$3\b/i, label: 'Betting Stakes' },
  { regex: /\b\$2\/\$5\b/i, label: 'Betting Stakes' },
  { regex: /\bbuy[-\s]?in\b/i, label: 'Buy-in' },
  { regex: /\bhigh[-\s]?stakes\b/i, label: 'High-Stakes' },
];

const DEFAULT_SMS_TEMPLATE = `EPL: Cards, cocktails, food, and a great crowd — starting today at 6 PM.

Join us for our Monthly Classic Championship tonight!

View details: https://www.elitepokerlounge.com/assets/uploads/events_tournaments/695bb883dfea3.webp

Msg & data rates may apply.

Reply STOP to opt out, HELP for help.

Terms: https://elitepokerlounge.com/terms | Privacy: https://elitepokerlounge.com/privacy`;

const getPlayerId = (player) => {
  if (player?.id) return player.id.toString();
  return `${player?.player_number || 'no-number'}-${player?.player_name || 'no-name'}-${player?.phone || 'no-phone'}`;
};

export default function BulkSmsMessaging({
  isOpen,
  onClose,
  loadPlayers,
  sendAll = null,
  onSchedule = null,
  activePlatform = 'twilio',
  gatewayBalance = null,
  onSwitchPlatform = null,
}) {
  // Target Audience Mode: 'all' (all active/opt-in) or 'custom' (directory / manual numbers)
  const [targetMode, setTargetMode] = useState(sendAll ? 'all' : 'custom');
  const [customAudienceTab, setCustomAudienceTab] = useState('directory'); // 'directory' | 'manual'

  // Message & Composer
  const [messageText, setMessageText] = useState(DEFAULT_SMS_TEMPLATE);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Real-time Carrier Deliverability Risk Detection (A2P 10DLC SHAFT / Gambling Filtering)
  const detectedCarrierRisks = useMemo(() => {
    if (!messageText) return [];
    return CARRIER_FLAGGED_PATTERNS.filter((p) => p.regex.test(messageText)).map((p) => p.label);
  }, [messageText]);

  // Directory Selection
  const [smsPlayers, setSmsPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [directoryFilter, setDirectoryFilter] = useState('all'); // 'all' | 'active_only'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Manual Numbers
  const [manualPhoneInput, setManualPhoneInput] = useState('');
  const [manualNumbersList, setManualNumbersList] = useState([]);
  const [manualInputError, setManualInputError] = useState('');

  // Player ID / CSV Lookup
  const [playerNumberInput, setPlayerNumberInput] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [playerListRecipients, setPlayerListRecipients] = useState([]);
  const [lookupSummary, setLookupSummary] = useState(null);
  const [lookupError, setLookupError] = useState('');

  // Confirmation & Dispatch states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showOptimizerModal, setShowOptimizerModal] = useState(false);
  const [showPresetsModal, setShowPresetsModal] = useState(false);

  // Active Players Roster Viewer
  const [showActiveList, setShowActiveList] = useState(false);
  const [activePlayersRoster, setActivePlayersRoster] = useState([]);
  const [activePlayersLoading, setActivePlayersLoading] = useState(false);
  const [activeSearchTerm, setActiveSearchTerm] = useState('');

  const handleToggleActiveList = async () => {
    const nextState = !showActiveList;
    setShowActiveList(nextState);
    if (nextState && activePlayersRoster.length === 0) {
      setActivePlayersLoading(true);
      try {
        if (sendAll && typeof sendAll.fetchRecipients === 'function') {
          const res = await sendAll.fetchRecipients();
          const players = res?.players || [];
          setActivePlayersRoster(players);
        } else {
          const res = await playerProfileService.getActivePlayersPhones();
          const players = res?.data?.data?.players || [];
          setActivePlayersRoster(players);
        }
      } catch (err) {
        console.error('Failed to load active players roster:', err);
        toast.error('Failed to load active players list');
      } finally {
        setActivePlayersLoading(false);
      }
    }
  };

  const filteredActivePlayers = useMemo(() => {
    if (!activeSearchTerm.trim()) return activePlayersRoster;
    const term = activeSearchTerm.toLowerCase().trim();
    return activePlayersRoster.filter((p) => {
      const name = String(p.player_name || '').toLowerCase();
      const num = String(p.player_number || '').toLowerCase();
      const ph = String(p.phone || '').replace(/\D/g, '');
      return name.includes(term) || num.includes(term) || ph.includes(term);
    });
  }, [activePlayersRoster, activeSearchTerm]);

  // Auto-fetch active players roster when all or active_plus_custom is selected
  useEffect(() => {
    if (isOpen && (targetMode === 'all' || targetMode === 'active_plus_custom') && activePlayersRoster.length === 0) {
      let cancelled = false;
      const loadActive = async () => {
        try {
          if (sendAll && typeof sendAll.fetchRecipients === 'function') {
            const res = await sendAll.fetchRecipients();
            if (!cancelled) setActivePlayersRoster(res?.players || []);
          } else {
            const res = await playerProfileService.getActivePlayersPhones();
            if (!cancelled) setActivePlayersRoster(res?.data?.data?.players || []);
          }
        } catch (e) {
          // ignore error
        }
      };
      loadActive();
      return () => {
        cancelled = true;
      };
    }
  }, [isOpen, targetMode, activePlayersRoster.length, sendAll]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load template and players on open
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const loadData = async () => {
      setPlayersLoading(true);

      // Load saved template
      try {
        const res = await playerProfileService.getSmsTemplate('default');
        const body = res?.data?.data?.body ?? res?.data?.body;
        if (!cancelled && res?.success && body != null) {
          setMessageText(body);
        }
      } catch {
        if (!cancelled) setMessageText(DEFAULT_SMS_TEMPLATE);
      }

      // Load player directory
      if (typeof loadPlayers === 'function') {
        try {
          const players = await loadPlayers();
          if (!cancelled) {
            setSmsPlayers(Array.isArray(players) ? players : []);
          }
        } catch (err) {
          console.error('Failed to load SMS players:', err);
          if (!cancelled) toast.error('Failed to load players directory');
        }
      }

      if (!cancelled) setPlayersLoading(false);
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [isOpen, loadPlayers]);

  // Reset when closing
  const handleClose = useCallback(() => {
    setShowConfirmModal(false);
    setShowResultModal(false);
    setSendResult(null);
    setSelectedPlayers(new Set());
    setManualNumbersList([]);
    setManualPhoneInput('');
    setManualInputError('');
    setPlayerNumberInput('');
    setPlayerListRecipients([]);
    setLookupSummary(null);
    setLookupError('');
    setSearchTerm('');
    setDebouncedSearch('');
    onClose?.();
  }, [onClose]);

  // Normalize phone to +1XXXXXXXXXX
  const normalizePhone = useCallback((phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, '');
    if (cleaned.length === 10) return `+1${cleaned}`;
    if (cleaned.length === 11 && cleaned.startsWith('1')) return `+${cleaned}`;
    if (cleaned.length > 11 && String(phone).trim().startsWith('+')) return `+${cleaned}`;
    return null;
  }, []);

  // Filter directory players
  const filteredPlayers = useMemo(() => {
    let list = (smsPlayers || []).filter((p) => {
      if (!p.phone || !p.phone.trim()) return false;
      if (!p.player_name || p.player_name.trim().length < 2) return false;
      return true;
    });

    // Remove duplicates
    const seen = new Set();
    list = list.filter((p) => {
      const key = `${p.player_number || 'none'}-${p.player_name?.trim()}-${p.phone?.trim()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Active filter
    if (directoryFilter === 'active_only') {
      list = list.filter((p) => Boolean(p.open_date && !p.closed_date));
    }

    // Search filter
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase().trim();
      list = list.filter(
        (p) =>
          p.player_name?.toLowerCase().includes(q) ||
          p.phone?.toLowerCase().includes(q) ||
          String(p.player_number || '').includes(q)
      );
    }

    return list;
  }, [smsPlayers, directoryFilter, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredPlayers.length / itemsPerPage));
  const paginatedPlayers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredPlayers.slice(start, start + itemsPerPage);
  }, [filteredPlayers, currentPage, itemsPerPage]);

  // Directory Selection Handlers
  const handleTogglePlayer = (playerId) => {
    const next = new Set(selectedPlayers);
    if (next.has(playerId)) next.delete(playerId);
    else next.add(playerId);
    setSelectedPlayers(next);
  };

  const isPageAllSelected = useMemo(() => {
    if (paginatedPlayers.length === 0) return false;
    return paginatedPlayers.every((p) => selectedPlayers.has(getPlayerId(p)));
  }, [paginatedPlayers, selectedPlayers]);

  const handleToggleSelectPage = () => {
    const next = new Set(selectedPlayers);
    if (isPageAllSelected) {
      paginatedPlayers.forEach((p) => next.delete(getPlayerId(p)));
    } else {
      paginatedPlayers.forEach((p) => next.add(getPlayerId(p)));
    }
    setSelectedPlayers(next);
  };

  const handleClearSelectedPlayers = () => {
    setSelectedPlayers(new Set());
  };

  // Manual Phone Input Parsing
  const handleAddManualPhones = () => {
    if (!manualPhoneInput.trim()) return;

    const rawTokens = manualPhoneInput
      .split(/[\s,;\n\t]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (rawTokens.length === 0) {
      setManualInputError('Please enter at least one valid phone number.');
      return;
    }

    const validNew = [];
    let invalidCount = 0;

    rawTokens.forEach((token) => {
      const normalized = normalizePhone(token);
      if (normalized) {
        if (!manualNumbersList.includes(normalized) && !validNew.includes(normalized)) {
          validNew.push(normalized);
        }
      } else {
        invalidCount++;
      }
    });

    if (validNew.length === 0) {
      setManualInputError(
        invalidCount > 0
          ? `Could not parse valid USA phone numbers (${invalidCount} invalid). Use 10 digits.`
          : 'Numbers are already added.'
      );
      return;
    }

    setManualNumbersList((prev) => [...prev, ...validNew]);
    setManualPhoneInput('');
    setManualInputError('');
    toast.success(
      `Added ${validNew.length} phone number(s)${invalidCount > 0 ? ` (${invalidCount} skipped)` : ''}`
    );
  };

  const handleRemoveManualNumber = (numToRemove) => {
    setManualNumbersList((prev) => prev.filter((n) => n !== numToRemove));
  };

  // Player ID / CSV Lookup Handlers
  const handleLookupPlayers = async () => {
    if (!playerNumberInput.trim()) {
      setLookupError('Please enter at least one player number or upload a CSV');
      return;
    }
    setIsLookingUp(true);
    setLookupError('');
    try {
      const res = await playerProfileService.lookupPlayersForSms(playerNumberInput);
      if (res?.success) {
        const data = res?.data?.data || res?.data || {};
        const recipients = data.recipients || [];
        setPlayerListRecipients(recipients);
        setLookupSummary(data.summary || null);
        if (recipients.length > 0) {
          toast.success(`Matched ${recipients.length} player(s) with valid phone numbers!`);
        } else {
          toast.error('No matching players with valid sendable phones found.');
        }
      } else {
        setLookupError(res?.error || 'Failed to lookup player list');
      }
    } catch (err) {
      setLookupError(err?.message || 'Error occurred while looking up players');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result || '';
      setPlayerNumberInput(text);
      setLookupError('');
      toast.success(`Loaded "${file.name}" into player numbers input`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleClearPlayerList = () => {
    setPlayerNumberInput('');
    setPlayerListRecipients([]);
    setLookupSummary(null);
    setLookupError('');
  };

  // Compile total custom recipients
  const customRecipients = useMemo(() => {
    const list = [];
    const seen = new Set();

    // 1. From directory
    smsPlayers.forEach((p) => {
      const id = getPlayerId(p);
      if (selectedPlayers.has(id)) {
        const norm = normalizePhone(p.phone);
        if (norm && !seen.has(norm)) {
          seen.add(norm);
          list.push({
            player_id: p.id || null,
            player_number: p.player_number || null,
            player_name: p.player_name || null,
            phone: norm
          });
        }
      }
    });

    // 2. From Player ID / CSV lookup
    playerListRecipients.forEach((p) => {
      const norm = normalizePhone(p.phone);
      if (norm && !seen.has(norm)) {
        seen.add(norm);
        list.push({
          player_id: p.player_id || null,
          player_number: p.player_number || null,
          player_name: p.player_name || null,
          phone: norm
        });
      }
    });

    // 3. From manual numbers
    manualNumbersList.forEach((num) => {
      if (!seen.has(num)) {
        seen.add(num);
        list.push({
          player_name: 'Manual Recipient',
          phone: num
        });
      }
    });

    return list;
  }, [smsPlayers, selectedPlayers, playerListRecipients, manualNumbersList, normalizePhone]);

  // Deduplicated combined count for Active + Extra mode
  const combinedRecipientsCount = useMemo(() => {
    if (targetMode === 'all') {
      return activePlayersRoster.length > 0 ? activePlayersRoster.length : (sendAll?.count || 0);
    }
    if (targetMode === 'custom') {
      return customRecipients.length;
    }
    // active_plus_custom
    const phoneSet = new Set();
    activePlayersRoster.forEach((p) => {
      const norm = normalizePhone(p.phone);
      if (norm) phoneSet.add(norm);
    });
    customRecipients.forEach((p) => {
      const norm = normalizePhone(p.phone);
      if (norm) phoneSet.add(norm);
    });
    if (phoneSet.size === 0 && (sendAll?.count || 0) > 0) {
      return (sendAll?.count || 0) + customRecipients.length;
    }
    return phoneSet.size;
  }, [targetMode, activePlayersRoster, sendAll, customRecipients, normalizePhone]);

  // Character and SMS Segment Count
  const charCount = messageText.length;
  const segments = Math.ceil(charCount / 160) || 1;

  // Save template to DB
  const handleSaveTemplate = async () => {
    if (!messageText.trim()) {
      toast.error('Message body is empty');
      return;
    }
    setSavingTemplate(true);
    try {
      const res = await playerProfileService.saveSmsTemplate(messageText.trim(), 'default');
      if (res?.success) {
        toast.success('Default SMS template saved!');
      } else {
        toast.error(res?.error || 'Failed to save template');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Schedule Navigation
  const handleOpenSchedule = () => {
    if (!onSchedule) return;
    const recipientsForSchedule = targetMode === 'all' ? [] : customRecipients;
    const resolvedTargetType = targetMode === 'all' ? 'all_active' : targetMode === 'active_plus_custom' ? 'active_and_custom' : 'custom';
    onSchedule(messageText, recipientsForSchedule, resolvedTargetType);
  };

  // Trigger Send Confirmation
  const handleInitiateSend = () => {
    if (!messageText.trim()) {
      toast.error('Please enter a message to send');
      return;
    }

    if (targetMode === 'custom' && customRecipients.length === 0) {
      toast.error('Please select players or add phone numbers first');
      return;
    }

    setShowConfirmModal(true);
  };

  // Final Dispatch
  const handleConfirmAndSend = async () => {
    setShowConfirmModal(false);
    setIsSending(true);

    try {
      let recipientsToSend = [];

      if (targetMode === 'all') {
        if (!sendAll?.fetchRecipients) {
          throw new Error('All-active send handler not configured');
        }
        const payload = await sendAll.fetchRecipients();
        const players = payload?.players || [];
        const phoneNumbers = payload?.phoneNumbers || [];

        if (players.length === 0 && phoneNumbers.length === 0) {
          toast.error(sendAll.emptyError || 'No active players with phone numbers found');
          setIsSending(false);
          return;
        }

        recipientsToSend = players.length > 0
          ? players
              .map((p) => {
                const norm = normalizePhone(p.phone);
                if (!norm) return null;
                return {
                  player_id: p.player_id || p.id || null,
                  player_number: p.player_number || null,
                  player_name: p.player_name || null,
                  phone: norm
                };
              })
              .filter(Boolean)
          : phoneNumbers;
      } else if (targetMode === 'active_plus_custom') {
        let activePlayers = [];
        if (sendAll && typeof sendAll.fetchRecipients === 'function') {
          const payload = await sendAll.fetchRecipients();
          activePlayers = payload?.players || [];
        } else {
          const res = await playerProfileService.getActivePlayersPhones();
          activePlayers = res?.data?.data?.players || [];
        }

        const phoneMap = new Map();
        activePlayers.forEach((p) => {
          const norm = normalizePhone(p.phone);
          if (norm && !phoneMap.has(norm)) {
            phoneMap.set(norm, {
              player_id: p.player_id || p.id || null,
              player_number: p.player_number || null,
              player_name: p.player_name || null,
              phone: norm
            });
          }
        });

        customRecipients.forEach((p) => {
          const norm = normalizePhone(p.phone);
          if (norm && !phoneMap.has(norm)) {
            phoneMap.set(norm, {
              player_id: p.player_id || null,
              player_number: p.player_number || null,
              player_name: p.player_name || 'Additional Recipient',
              phone: norm
            });
          }
        });

        recipientsToSend = Array.from(phoneMap.values());
      } else {
        recipientsToSend = customRecipients;
      }

      if (recipientsToSend.length === 0) {
        toast.error('No valid phone numbers found to send');
        setIsSending(false);
        return;
      }

      const response = await playerProfileService.sendBulkSMS(recipientsToSend, messageText);
      const payload = getBulkSmsPayload(response);
      toastBulkSmsResult(toast, payload);

      setSendResult({
        total: payload.total || recipientsToSend.length,
        successCount: payload.successCount || 0,
        failCount: payload.failCount || 0,
        skipCount: payload.skipCount || 0,
        failed: payload.failed || [],
        skipped: payload.skipped || []
      });
      setShowResultModal(true);
    } catch (err) {
      console.error('Send error:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to send messages');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 animate-in fade-in duration-200">
        <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-gray-100">
          
          {/* MODAL HEADER - BLACK, WHITE TEXT WITH YELLOW ACCENT */}
          <div className="px-6 py-4 bg-black text-white flex items-center justify-between border-b border-gray-800 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-400 rounded-xl shadow-md">
                <MessageSquare className="h-6 w-6 text-gray-950" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold tracking-tight text-white">SMS Broadcast Hub</h2>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/40">
                    Live Messaging
                  </span>
                  {onSwitchPlatform ? (
                    <select
                      value={activePlatform}
                      onChange={(e) => onSwitchPlatform(e.target.value)}
                      className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-900 text-amber-300 border border-amber-400/40 focus:outline-none cursor-pointer"
                      title="Switch SMS Gateway Platform"
                    >
                      <option value="twilio">Gateway: Twilio (Default)</option>
                      <option value="telnyx">Gateway: Telnyx</option>
                    </select>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-900 text-amber-300 border border-amber-400/40 capitalize">
                      Gateway: {activePlatform || 'Twilio'}
                    </span>
                  )}
                  {gatewayBalance && gatewayBalance.balance != null && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-bold border flex items-center gap-1 ${
                        gatewayBalance.balance < 5
                          ? 'bg-amber-500/20 text-amber-300 border-amber-400/50'
                          : 'bg-emerald-500/20 text-emerald-300 border-emerald-400/50'
                      }`}
                      title={`${activePlatform.toUpperCase()} Balance: $${Number(gatewayBalance.balance).toFixed(2)} ${gatewayBalance.currency || 'USD'}${
                        gatewayBalance.availableCredit != null ? ` (Available credit: $${Number(gatewayBalance.availableCredit).toFixed(2)})` : ''
                      }`}
                    >
                      💰 ${Number(gatewayBalance.balance).toFixed(2)} {gatewayBalance.currency || 'USD'}
                      {gatewayBalance.isMock && <span className="text-[9px] opacity-75 font-normal">(mock)</span>}
                      {gatewayBalance.balance < 5 && <span className="text-[9px] px-1 bg-amber-400 text-gray-950 rounded font-black">LOW</span>}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-300 mt-0.5">
                  Compose, preview, and dispatch text messages to active club members or selected players
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              disabled={isSending}
              className="text-gray-400 hover:text-amber-400 p-1.5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
              title="Close"
            >
              <X size={22} />
            </button>
          </div>

          {/* MAIN MODAL BODY - 2-COLUMN USER-FRIENDLY LAYOUT */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/50">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

              {/* LEFT COLUMN: AUDIENCE SELECTION (5 cols) */}
              <div className="lg:col-span-5 flex flex-col gap-4">
                <div className="bg-white p-4 rounded-xl border border-gray-200/90 shadow-sm space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-400 text-black font-black text-xs shadow-sm">
                        1
                      </span>
                      <h3 className="text-sm font-bold text-gray-900">Choose Audience</h3>
                    </div>

                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-900 border border-yellow-300 shadow-sm">
                      {targetMode === 'all'
                        ? sendAll?.label || 'All Active Members'
                        : targetMode === 'active_plus_custom'
                        ? `Active + Extra (${combinedRecipientsCount})`
                        : `${customRecipients.length} Recipient(s)`}
                    </span>
                  </div>

                  {/* Top-Level Mode Selector - Yellow & Black Theme */}
                  <div className="grid grid-cols-3 gap-1 bg-gray-100 p-1 rounded-xl">
                    {sendAll && (
                      <button
                        type="button"
                        onClick={() => setTargetMode('all')}
                        className={`py-2 px-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition-all ${
                          targetMode === 'all'
                            ? 'bg-yellow-400 text-black shadow-sm'
                            : 'text-gray-600 hover:text-black'
                        }`}
                        title="Send strictly to all members currently active in the club"
                      >
                        <UserCheck size={14} />
                        All Active
                      </button>
                    )}

                    {sendAll && (
                      <button
                        type="button"
                        onClick={() => setTargetMode('active_plus_custom')}
                        className={`py-2 px-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition-all ${
                          targetMode === 'active_plus_custom'
                            ? 'bg-yellow-400 text-black shadow-sm'
                            : 'text-gray-600 hover:text-black'
                        }`}
                        title="Send to all active members PLUS extra custom people (no duplicates)"
                      >
                        <UserPlus size={14} />
                        Active + Extra
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setTargetMode('custom')}
                      className={`py-2 px-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition-all ${
                        targetMode === 'custom' || !sendAll
                          ? 'bg-yellow-400 text-black shadow-sm'
                          : 'text-gray-600 hover:text-black'
                      }`}
                      title="Send only to specific players from directory or typed phone numbers"
                    >
                      <Users size={14} />
                      Specific Only
                    </button>
                  </div>

                  {/* MODE A: ALL ACTIVE PLAYERS */}
                  {targetMode === 'all' && sendAll && (
                    <div className="p-4 bg-gradient-to-br from-emerald-50/80 to-teal-50/40 rounded-xl border border-emerald-200/80 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-emerald-100 text-emerald-800 rounded-lg mt-0.5">
                          <CheckCircle2 size={18} />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-emerald-950">
                            {sendAll.label || 'All Active Club Members'}
                          </h4>
                          <p className="text-xs text-emerald-800/80 mt-1 leading-relaxed">
                            {sendAll.description ||
                              'Dispatches directly to every active player who has a valid phone number on record.'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-emerald-200/60 text-xs">
                        <span className="text-emerald-900 font-medium">Estimated Audience:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-emerald-800 bg-white px-2.5 py-0.5 rounded-full border border-emerald-200">
                            ~{activePlayersRoster.length > 0 ? activePlayersRoster.length : sendAll.count || 'Active'} Players
                          </span>
                          <button
                            type="button"
                            onClick={handleToggleActiveList}
                            className="text-xs font-semibold text-emerald-800 hover:text-emerald-950 bg-emerald-100 hover:bg-emerald-200 px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors"
                          >
                            {showActiveList ? <EyeOff size={13} /> : <Eye size={13} />}
                            {showActiveList ? 'Hide Active Players' : 'Show Active Players'}
                          </button>
                        </div>
                      </div>

                      {/* ACTIVE PLAYERS ROSTER DRAWER */}
                      {showActiveList && (
                        <div className="mt-3 bg-white border border-emerald-200 rounded-xl p-3 shadow-sm space-y-2.5 animate-in fade-in duration-150">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold text-gray-800">
                              Active Players Roster ({filteredActivePlayers.length}
                              {activePlayersRoster.length !== filteredActivePlayers.length ? ` of ${activePlayersRoster.length}` : ''})
                            </span>
                            {activePlayersLoading && (
                              <Loader2 size={14} className="animate-spin text-emerald-600" />
                            )}
                          </div>

                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                              type="text"
                              value={activeSearchTerm}
                              onChange={(e) => setActiveSearchTerm(e.target.value)}
                              placeholder="Search active player by name, # or phone..."
                              className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            />
                            {activeSearchTerm && (
                              <button
                                type="button"
                                onClick={() => setActiveSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>

                          {activePlayersLoading ? (
                            <div className="py-6 text-center text-xs text-gray-500 flex flex-col items-center justify-center gap-1.5">
                              <Loader2 size={18} className="animate-spin text-emerald-600" />
                              <span>Loading active players list...</span>
                            </div>
                          ) : filteredActivePlayers.length === 0 ? (
                            <div className="py-4 text-center text-xs text-gray-500">
                              {activeSearchTerm ? 'No active players match search' : 'No active players found with valid phones'}
                            </div>
                          ) : (
                            <div className="max-h-52 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                              {filteredActivePlayers.map((player, idx) => (
                                <div
                                  key={player.player_id || player.phone || idx}
                                  className="px-3 py-2 flex items-center justify-between text-xs hover:bg-gray-50/80 transition-colors"
                                >
                                  <div>
                                    <div className="font-semibold text-gray-900 flex items-center gap-1.5">
                                      <span>{player.player_name || 'Active Member'}</span>
                                      {player.player_number && (
                                        <span className="text-[11px] font-mono text-gray-500 bg-gray-100 px-1.5 py-0.2 rounded">
                                          #{player.player_number}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[11px] font-mono text-gray-500 mt-0.5">
                                      {player.phone}
                                    </div>
                                  </div>
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
                                    Active
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <p className="text-[11px] text-emerald-700 italic">
                        Tip: If you only want to send to a few specific players, switch to "Custom Selection" above.
                      </p>
                    </div>
                  )}

                  {/* MODE B: COMBINED ACTIVE + EXTRA BANNER */}
                  {targetMode === 'active_plus_custom' && (
                    <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-xl space-y-2 animate-in fade-in">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-900">
                          <CheckCircle2 size={16} className="text-yellow-600 flex-shrink-0" />
                          <span>All Active Club Players Included (~{activePlayersRoster.length > 0 ? activePlayersRoster.length : sendAll?.count || 'Active'} Players)</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleToggleActiveList}
                          className="text-xs font-bold text-gray-900 bg-yellow-200 hover:bg-yellow-300 px-2.5 py-0.5 rounded-lg flex items-center gap-1 transition-colors"
                        >
                          {showActiveList ? <EyeOff size={12} /> : <Eye size={12} />}
                          {showActiveList ? 'Hide Active Roster' : 'View Roster'}
                        </button>
                      </div>

                      {showActiveList && (
                        <div className="bg-white border border-yellow-200 rounded-xl p-3 shadow-sm space-y-2">
                          <div className="text-xs font-bold text-gray-800">
                            Active Members ({filteredActivePlayers.length})
                          </div>
                          <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                            {filteredActivePlayers.map((player, idx) => (
                              <div key={idx} className="px-3 py-1.5 flex items-center justify-between text-xs hover:bg-gray-50">
                                <div>
                                  <span className="font-semibold text-gray-900">{player.player_name || 'Active Member'}</span>
                                  {player.player_number && <span className="text-gray-400 font-mono ml-1">#{player.player_number}</span>}
                                </div>
                                <span className="font-mono text-gray-500 text-[11px]">{player.phone}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <p className="text-[11px] text-gray-700">
                        Select additional players or type extra numbers below. Duplicate phone numbers are automatically removed so no player receives duplicate texts!
                      </p>
                    </div>
                  )}

                  {/* CUSTOM OR EXTRA AUDIENCE PICKER */}
                  {(targetMode === 'custom' || targetMode === 'active_plus_custom') && (
                    <div className="space-y-3">
                      {/* Sub-Tabs: Directory vs Player IDs / CSV vs Manual */}
                      <div className="flex border-b border-gray-200">
                        <button
                          type="button"
                          onClick={() => setCustomAudienceTab('directory')}
                          className={`flex-1 pb-2 text-xs font-semibold border-b-2 text-center transition-colors ${
                            customAudienceTab === 'directory'
                              ? 'border-yellow-400 text-black font-bold'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          All Players Directory ({selectedPlayers.size > 0 ? `${selectedPlayers.size} selected` : '3,675'})
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomAudienceTab('playerList')}
                          className={`flex-1 pb-2 text-xs font-semibold border-b-2 text-center transition-colors ${
                            customAudienceTab === 'playerList'
                              ? 'border-yellow-400 text-black font-bold'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Player IDs / CSV ({playerListRecipients.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomAudienceTab('manual')}
                          className={`flex-1 pb-2 text-xs font-semibold border-b-2 text-center transition-colors ${
                            customAudienceTab === 'manual'
                              ? 'border-yellow-400 text-black font-bold'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Manual Numbers ({manualNumbersList.length})
                        </button>
                      </div>

                      {/* SUB-VIEW 1: DIRECTORY */}
                      {customAudienceTab === 'directory' && (
                        <div className="space-y-3">
                          {/* Explanatory banner */}
                          <div className="p-2.5 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-950 leading-relaxed">
                            <span className="font-semibold">Master Player Directory:</span> Showing all 3,675 accounts ever registered in the club database. Check boxes to message specific individuals.
                            <span className="block text-[11px] text-yellow-800 mt-0.5">
                              💡 To broadcast to <strong>only currently active players</strong>, select the <strong>"All Active"</strong> button above.
                            </span>
                          </div>

                          {/* Search & Filter */}
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                              />
                              <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, #, phone..."
                                className="w-full pl-8 pr-7 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none"
                              />
                              {searchTerm && (
                                <button
                                  type="button"
                                  onClick={() => setSearchTerm('')}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>

                            <select
                              value={directoryFilter}
                              onChange={(e) => {
                                setDirectoryFilter(e.target.value);
                                setCurrentPage(1);
                              }}
                              className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-gray-700"
                            >
                              <option value="all">All Registered (3,675)</option>
                              <option value="active_only">Open Table / Session Only</option>
                            </select>
                          </div>

                          {/* Selection Actions */}
                          <div className="flex items-center justify-between text-xs px-1">
                            <button
                              type="button"
                              onClick={handleToggleSelectPage}
                              className="text-gray-900 hover:text-black font-bold flex items-center gap-1"
                            >
                              {isPageAllSelected ? <Square size={13} /> : <CheckSquare size={13} />}
                              {isPageAllSelected ? 'Deselect Page' : 'Select Page'}
                            </button>

                            {selectedPlayers.size > 0 && (
                              <button
                                type="button"
                                onClick={handleClearSelectedPlayers}
                                className="text-rose-600 hover:text-rose-800 font-medium"
                              >
                                Clear All ({selectedPlayers.size})
                              </button>
                            )}
                          </div>

                          {/* Player List */}
                          <div className="border border-gray-200 rounded-xl max-h-56 overflow-y-auto divide-y divide-gray-100 bg-white">
                            {playersLoading ? (
                              <div className="py-10 text-center text-gray-400 text-xs">
                                <Loader2 size={18} className="animate-spin mx-auto mb-1 text-blue-600" />
                                Loading directory...
                              </div>
                            ) : paginatedPlayers.length === 0 ? (
                              <div className="py-8 text-center text-gray-400 text-xs px-4">
                                No players found matching "{searchTerm}"
                              </div>
                            ) : (
                              paginatedPlayers.map((player) => {
                                const id = getPlayerId(player);
                                const isSelected = selectedPlayers.has(id);
                                const isActive = Boolean(player.open_date && !player.closed_date);

                                return (
                                  <div
                                    key={id}
                                    onClick={() => handleTogglePlayer(id)}
                                    className={`px-3 py-2 flex items-center justify-between hover:bg-gray-50 cursor-pointer text-xs transition-colors ${
                                      isSelected ? 'bg-yellow-50/80 border-l-2 border-yellow-400' : ''
                                    }`}
                                  >
                                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}}
                                        className="h-3.5 w-3.5 text-yellow-500 accent-yellow-400 rounded border-gray-300 pointer-events-none"
                                      />
                                      <div className="truncate">
                                        <div className="font-semibold text-gray-900 truncate">
                                          {player.player_name}
                                          {player.player_number && (
                                            <span className="text-gray-400 font-normal ml-1">
                                              #{player.player_number}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-[11px] text-gray-500 font-mono">
                                          {player.phone}
                                        </div>
                                      </div>
                                    </div>

                                    {isActive && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800">
                                        Active
                                      </span>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>

                          {/* Pagination */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
                              <span>
                                Page {currentPage} of {totalPages} ({filteredPlayers.length})
                              </span>
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                  disabled={currentPage <= 1}
                                  className="px-2 py-0.5 border rounded disabled:opacity-40"
                                >
                                  Prev
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                  disabled={currentPage >= totalPages}
                                  className="px-2 py-0.5 border rounded disabled:opacity-40"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* SUB-VIEW 2: PLAYER ID / CSV LIST */}
                      {customAudienceTab === 'playerList' && (
                        <div className="space-y-3">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-semibold text-gray-700">
                                Paste Player Numbers or Upload File:
                              </label>
                              <label className="cursor-pointer inline-flex items-center gap-1 text-[11px] font-semibold text-gray-900 hover:text-black bg-yellow-100 hover:bg-yellow-200 border border-yellow-300 font-bold px-2 py-0.5 rounded transition-colors">
                                <Upload size={12} />
                                Upload .CSV / .TXT
                                <input
                                  type="file"
                                  accept=".csv,.txt"
                                  className="hidden"
                                  onChange={handleFileUpload}
                                />
                              </label>
                            </div>
                            <textarea
                              value={playerNumberInput}
                              onChange={(e) => {
                                setPlayerNumberInput(e.target.value);
                                setLookupError('');
                              }}
                              placeholder="Paste Player Numbers separated by commas, spaces, or lines&#10;e.g. 1001, 1002, 1045, TEST001"
                              rows={3}
                              className={`w-full p-2.5 text-xs font-mono border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none ${
                                lookupError ? 'border-rose-400 bg-rose-50/50' : 'border-gray-300'
                              }`}
                            />
                            {lookupError && (
                              <p className="text-[11px] text-rose-600 mt-1">{lookupError}</p>
                            )}

                            <div className="flex items-center justify-between mt-1.5">
                              {playerListRecipients.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={handleClearPlayerList}
                                  className="text-rose-600 hover:text-rose-800 text-[11px] font-medium"
                                >
                                  Clear Loaded List
                                </button>
                              ) : <span />}

                              <button
                                type="button"
                                onClick={handleLookupPlayers}
                                disabled={isLookingUp || !playerNumberInput.trim()}
                                className="px-3.5 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-black text-xs font-bold rounded-lg flex items-center gap-1.5 disabled:opacity-50 transition-all shadow-sm"
                              >
                                {isLookingUp ? (
                                  <>
                                    <Loader2 size={13} className="animate-spin" />
                                    Matching in DB...
                                  </>
                                ) : (
                                  <>
                                    <UserCheck size={13} />
                                    Verify & Load Players
                                  </>
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Lookup Summary Pill Badges */}
                          {lookupSummary && (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                              <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
                                <span>Verification Results:</span>
                                <span className="text-gray-500 font-normal">
                                  {lookupSummary.totalRequested} requested
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                                  <span className="text-emerald-700 font-medium block">Valid Recipients</span>
                                  <span className="text-lg font-bold text-emerald-800">
                                    {lookupSummary.validToSend}
                                  </span>
                                </div>
                                <div className="p-2 bg-amber-50 border border-amber-200 rounded-md">
                                  <span className="text-amber-700 font-medium block">No Phone On File</span>
                                  <span className="text-lg font-bold text-amber-800">
                                    {lookupSummary.missingPhone}
                                  </span>
                                </div>
                                <div className="p-2 bg-purple-50 border border-purple-200 rounded-md">
                                  <span className="text-purple-700 font-medium block">Unsubscribed / Blocked</span>
                                  <span className="text-lg font-bold text-purple-800">
                                    {lookupSummary.unsubscribed}
                                  </span>
                                </div>
                                <div className="p-2 bg-gray-100 border border-gray-200 rounded-md">
                                  <span className="text-gray-600 font-medium block">Not Found in DB</span>
                                  <span className="text-lg font-bold text-gray-800">
                                    {lookupSummary.notFound}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* List of Loaded Players */}
                          {playerListRecipients.length > 0 && (
                            <div className="space-y-1.5">
                              <span className="text-xs font-semibold text-gray-700 block">
                                Ready to receive ({playerListRecipients.length}):
                              </span>
                              <div className="max-h-36 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white">
                                {playerListRecipients.map((recip, idx) => (
                                  <div key={idx} className="px-3 py-1.5 flex items-center justify-between text-xs hover:bg-gray-50">
                                    <div>
                                      <span className="font-semibold text-gray-900">{recip.player_name || 'Player'}</span>
                                      <span className="text-gray-500 ml-1.5">#{recip.player_number}</span>
                                    </div>
                                    <span className="font-mono text-gray-600 text-[11px]">{recip.phone}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* SUB-VIEW 3: MANUAL NUMBERS */}
                      {customAudienceTab === 'manual' && (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Add USA Numbers (Single or Paste List):
                            </label>
                            <textarea
                              value={manualPhoneInput}
                              onChange={(e) => {
                                setManualPhoneInput(e.target.value);
                                setManualInputError('');
                              }}
                              placeholder="Type or paste numbers separated by comma, space or newline&#10;e.g. 9565551234, 9565559876"
                              rows={3}
                              className={`w-full p-2.5 text-xs font-mono border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none ${
                                manualInputError ? 'border-rose-400 bg-rose-50/50' : 'border-gray-300'
                              }`}
                            />
                            {manualInputError && (
                              <p className="text-[11px] text-rose-600 mt-1">{manualInputError}</p>
                            )}
                            <div className="flex justify-end mt-1.5">
                              <button
                                type="button"
                                onClick={handleAddManualPhones}
                                disabled={!manualPhoneInput.trim()}
                                className="px-3.5 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-black text-xs font-bold rounded-lg flex items-center gap-1 disabled:opacity-50 transition-all shadow-sm"
                              >
                                <Plus size={13} />
                                Add Numbers
                              </button>
                            </div>
                          </div>

                          {/* List of Added Numbers */}
                          {manualNumbersList.length > 0 && (
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between text-xs text-gray-600">
                                <span className="font-semibold">
                                  Added Numbers ({manualNumbersList.length}):
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setManualNumbersList([])}
                                  className="text-rose-600 hover:text-rose-800 text-[11px]"
                                >
                                  Clear all
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                {manualNumbersList.map((num, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-yellow-100 text-yellow-950 border border-yellow-300 font-bold"
                                  >
                                    {num}
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveManualNumber(num)}
                                      className="text-yellow-800 hover:text-rose-700 ml-0.5"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN: COMPOSER & LIVE SMARTPHONE PREVIEW (7 cols) */}
              <div className="lg:col-span-7 flex flex-col gap-4">
                
                {/* MESSAGE COMPOSER */}
                <div className="bg-white p-4 rounded-xl border border-gray-200/90 shadow-sm space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-400 text-black font-black text-xs shadow-sm">
                        2
                      </span>
                      <h3 className="text-sm font-bold text-gray-900">Message Content</h3>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowPresetsModal(true)}
                        className="px-2.5 py-1 text-xs font-bold text-gray-800 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-300 flex items-center gap-1.5 transition-all shadow-sm"
                        title="Browse & insert pre-written SMS templates or your saved drafts"
                      >
                        <Library size={12} className="text-amber-600" />
                        <span>Presets Library</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowOptimizerModal(true)}
                        className="px-2.5 py-1 text-xs font-bold text-gray-900 bg-yellow-400 hover:bg-yellow-500 rounded-lg border border-yellow-500 flex items-center gap-1.5 transition-all shadow-sm"
                        title="Rewrite & optimize SMS with Claude AI (160 chars & policy check)"
                      >
                        <Sparkles size={12} className="text-gray-950" />
                        <span>AI Optimize</span>
                      </button>
                      <span className="text-gray-200">|</span>
                      <button
                        type="button"
                        onClick={() => setMessageText(DEFAULT_SMS_TEMPLATE)}
                        className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1 font-medium transition-colors"
                        title="Revert back to EPL standard promo template"
                      >
                        <RotateCcw size={12} />
                        Reset
                      </button>
                      <span className="text-gray-200">|</span>
                      <button
                        type="button"
                        onClick={handleSaveTemplate}
                        disabled={savingTemplate}
                        className="text-xs text-blue-600 hover:text-yellow-900 font-bold flex items-center gap-1 font-semibold transition-colors disabled:opacity-50"
                        title="Save as the default template for future broadcasts"
                      >
                        <Save size={12} />
                        {savingTemplate ? 'Saving...' : 'Save Default'}
                      </button>
                    </div>
                  </div>

                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type the SMS text message here..."
                    rows={6}
                    className="w-full p-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 focus:border-blue-500 outline-none leading-relaxed transition-all"
                  />

                  {/* Character & Segment Counter Bar */}
                  <div className="flex items-center justify-between text-xs px-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold ${
                          charCount > 160 ? 'text-amber-700' : 'text-gray-600'
                        }`}
                      >
                        {charCount} characters
                      </span>
                      <span className="text-gray-300">•</span>
                      <span className="text-gray-500">
                        {segments} SMS segment{segments > 1 ? 's' : ''}
                      </span>
                    </div>

                    {charCount > 160 && (
                      <span className="text-[11px] text-amber-700 font-medium">
                        (Carriers deliver messages &gt;160 chars in multi-segments)
                      </span>
                    )}
                  </div>

                  {/* Real-time Carrier Filter Risk Alert */}
                  {detectedCarrierRisks.length > 0 && (
                    <div className="mt-2.5 p-2.5 bg-amber-50 border border-amber-300 rounded-xl flex items-center justify-between gap-3 text-xs text-amber-950 shadow-sm animate-in fade-in">
                      <div className="flex items-center gap-2">
                        <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
                        <div>
                          <span className="font-bold">Carrier Filter Risk Detected:</span>{' '}
                          <span className="text-amber-800">
                            Draft contains {Array.from(new Set(detectedCarrierRisks)).join(', ')}. Telecom carriers frequently block messages with direct guarantees and betting stakes.
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowOptimizerModal(true)}
                        className="px-3 py-1 bg-amber-400 hover:bg-amber-500 text-gray-950 font-bold rounded-lg border border-amber-500 shadow-sm flex-shrink-0 flex items-center gap-1.5 transition-all text-xs"
                        title="Click to automatically convert into carrier-safe social club phrasing"
                      >
                        <Sparkles size={12} className="text-gray-950" />
                        Fix with AI
                      </button>
                    </div>
                  )}
                </div>

                {/* LIVE SMARTPHONE PREVIEW */}
                <div className="bg-white p-4 rounded-xl border border-gray-200/90 shadow-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                      <Smartphone size={14} className="text-yellow-600" />
                      Recipient Phone Preview
                    </div>
                    <span className="text-[10px] text-gray-400">Live preview of SMS</span>
                  </div>

                  {/* Phone frame simulation */}
                  <div className="bg-gradient-to-b from-gray-100 to-gray-200 p-3 rounded-2xl border border-gray-300 max-w-sm mx-auto shadow-inner">
                    <div className="bg-white rounded-xl p-3 min-h-[140px] max-h-[220px] overflow-y-auto flex flex-col justify-end shadow-sm">
                      <div className="text-center text-[10px] text-gray-400 font-semibold mb-2">
                        Today • Elite Poker Lounge
                      </div>

                      {/* Bubble */}
                      <div className="bg-gray-950 text-white text-xs p-3.5 rounded-2xl rounded-br-sm shadow-md leading-relaxed whitespace-pre-wrap break-words border border-gray-800">
                        {messageText.trim() ? (
                          messageText
                        ) : (
                          <span className="italic opacity-60">Message preview will appear here...</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* MODAL FOOTER */}
          <div className="px-6 py-4 bg-white border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-inner">
            <div className="text-xs text-gray-600 flex items-center gap-2">
              <span className="font-semibold text-gray-800">Target Summary:</span>
              <span className="bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200 font-medium text-gray-700">
                {targetMode === 'all'
                  ? sendAll?.label || 'All Active Players'
                  : targetMode === 'active_plus_custom'
                  ? `Active (~ ${activePlayersRoster.length > 0 ? activePlayersRoster.length : sendAll?.count || 'Active'}) + ${customRecipients.length} Extra (${combinedRecipientsCount} Unique Total)`
                  : `${customRecipients.length} Recipient(s)`}
              </span>
            </div>

            <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSending}
                className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>

              {onSchedule && (
                <button
                  type="button"
                  onClick={handleOpenSchedule}
                  disabled={isSending}
                  className="px-4 py-2 border border-amber-300 bg-amber-100 hover:bg-amber-200 text-amber-950 rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-colors"
                  title="Schedule this message for a future date/time"
                >
                  <Clock size={16} />
                  Schedule for Later
                </button>
              )}

              <button
                type="button"
                onClick={handleInitiateSend}
                disabled={
                  isSending ||
                  !messageText.trim() ||
                  (targetMode === 'custom' && customRecipients.length === 0)
                }
                className="px-5 py-2 bg-amber-400 hover:bg-amber-500 text-gray-950 rounded-xl text-sm font-bold shadow-md hover:shadow-lg flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <Loader2 size={16} className="animate-spin text-gray-950" />
                    Sending Broadcast...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Now ({targetMode === 'all' ? (activePlayersRoster.length > 0 ? activePlayersRoster.length : sendAll?.count || 'All') : targetMode === 'active_plus_custom' ? combinedRecipientsCount : customRecipients.length})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CONFIRMATION DIALOG */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <h3 className="text-base font-bold text-gray-900">Confirm SMS Broadcast</h3>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <p className="text-gray-600 leading-relaxed">
                You are about to dispatch a live SMS text message broadcast:
              </p>

              <div className="p-3 bg-yellow-50/80 rounded-xl border border-yellow-200 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-gray-900 font-semibold">Audience:</span>
                  <span className="font-bold text-yellow-900 font-bold">
                    {targetMode === 'all'
                      ? sendAll?.label || 'All Active Members'
                      : targetMode === 'active_plus_custom'
                      ? `Active Members + ${customRecipients.length} Extra (${combinedRecipientsCount} Total Unique)`
                      : `${customRecipients.length} Recipient(s)`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-900 font-semibold">Message Size:</span>
                  <span className="text-yellow-900 font-bold">
                    {charCount} chars • {segments} segment(s)
                  </span>
                </div>
              </div>

              <div>
                <span className="font-semibold text-gray-700 block mb-1">Message Preview:</span>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-800 max-h-32 overflow-y-auto whitespace-pre-wrap font-sans">
                  {messageText}
                </div>
              </div>

              <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200/80 text-amber-900 text-[11px]">
                Standard carrier text messaging charges apply. Live messages will be immediately delivered to recipients.
              </div>
            </div>

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:bg-gray-100"
              >
                Back to Edit
              </button>
              <button
                type="button"
                onClick={handleConfirmAndSend}
                className="px-5 py-2 bg-amber-400 hover:bg-amber-500 text-gray-950 rounded-xl text-xs font-bold shadow flex items-center gap-1.5 transition-all"
              >
                <Send size={14} />
                Confirm & Dispatch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RESULT DIALOG */}
      {showResultModal && sendResult && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <h3 className="text-base font-bold text-gray-900">Broadcast Results</h3>
              </div>
              <button
                onClick={() => {
                  setShowResultModal(false);
                  handleClose();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="text-2xl font-bold text-emerald-700">{sendResult.successCount}</div>
                  <div className="text-[11px] font-semibold text-emerald-800">Delivered</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="text-2xl font-bold text-amber-700">{sendResult.skipCount}</div>
                  <div className="text-[11px] font-semibold text-amber-800">Skipped</div>
                </div>
                <div className="p-3 bg-rose-50 rounded-xl border border-rose-200">
                  <div className="text-2xl font-bold text-rose-700">{sendResult.failCount}</div>
                  <div className="text-[11px] font-semibold text-rose-800">Failed</div>
                </div>
              </div>

              {sendResult.failed?.length > 0 && (
                <div>
                  <span className="text-xs font-bold text-rose-900 block mb-1">
                    Failed Numbers ({sendResult.failed.length}):
                  </span>
                  <div className="max-h-32 overflow-y-auto border border-rose-200 rounded-lg p-2 bg-rose-50/50 space-y-1 text-xs">
                    {sendResult.failed.map((f, i) => (
                      <div key={i} className="flex justify-between text-rose-800">
                        <span className="font-mono">{f.phone || 'Unknown'}</span>
                        <span className="text-rose-600 text-[11px]">{f.error || 'Failed'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowResultModal(false);
                  handleClose();
                }}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI MESSAGE OPTIMIZER MODAL */}
      <SmsMessageOptimizerModal
        isOpen={showOptimizerModal}
        onClose={() => setShowOptimizerModal(false)}
        initialMessage={messageText}
        onApplyMessage={(optMsg) => setMessageText(optMsg)}
        recipientContext={
          targetMode === 'all'
            ? 'All active Elite Poker Lounge club members in McAllen, Texas'
            : `${customRecipients.length} selected club players`
        }
      />

      {/* SMS PRESETS LIBRARY MODAL */}
      <SmsPresetsLibraryModal
        isOpen={showPresetsModal}
        onClose={() => setShowPresetsModal(false)}
        onSelectPreset={(presetBody) => setMessageText(presetBody)}
        onOpenOptimizer={(presetBody) => {
          setMessageText(presetBody);
          setShowOptimizerModal(true);
        }}
        currentDraftMessage={messageText}
      />
    </>
  );
}
